import { useState, type FormEvent } from "react";
import {
  Button,
  Card,
  DeleteWithConfirm,
  EmptyState,
  Inline,
  Stack,
  StatePill,
  TextInput,
} from "@genius-tracker/design-system";
import { useLocale } from "@genius-tracker/i18n/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, ApiError, apiSend } from "../lib/api.js";
import { formatDateTimeShortShort } from "../locale-format.js";

/**
 * Sprint 55.1 (F-55-104) — Tenant 360° devices card.
 * Sprint 55.2          — extend the card from read-only to full
 *                        platform-owner CRUD (create / archive /
 *                        move).
 *
 * Endpoints
 * =========
 *
 *   GET    /v1/admin/tenants/:id/devices               — list
 *   POST   /v1/admin/tenants/:id/devices               — create
 *          body: { imei, deviceType }
 *   DELETE /v1/admin/tenants/:id/devices/:deviceId     — archive (soft-delete)
 *   POST   /v1/admin/tenants/:id/devices/:deviceId/move — move
 *          body: { targetTenantId }
 *
 * Each action is gated to `platform.owner` by the API; the card
 * does not gate client-side beyond not rendering the buttons when
 * an action is in flight.
 *
 * UX notes
 * ========
 *
 * - Add device: inline mini-form below the table header.
 * - Move device: inline tenant picker per row (select dropdown
 *   populated from /v1/admin/tenants).
 * - Archive device: per-row button + native confirm() for the
 *   destructive action prompt.
 * - All actions invalidate the devices query on success so the
 *   table re-renders with the canonical post-action state.
 */

type DeviceIdentifierType = "imei" | "uuid" | "unknown";

interface AdminTenantDeviceRow {
  readonly id: string;
  readonly imei: string;
  // Sprint -07 — derived identifier type (imei / uuid). Drives the
  // delete-confirmation field label (IMEI vs UUID).
  readonly identifierType: DeviceIdentifierType;
  readonly deviceType: string;
  readonly status: string;
  readonly vehicleId: string | null;
  readonly lastSeenAt: string | null;
  readonly lastPacketAt: string | null;
  readonly approvedAt: string | null;
  readonly archivedAt: string | null;
  readonly createdAt: string;
}

interface AdminTenantDevicesResponse {
  readonly items: readonly AdminTenantDeviceRow[];
  readonly total: number;
  readonly capped: boolean;
}

interface AdminTenantRow {
  readonly id: string;
  readonly displayName: string | null;
}

interface AdminTenantsResponse {
  readonly items: readonly AdminTenantRow[];
}

// GT-DEVICE-IDENTIFIER-UUID-GENIUS-TRACKER-ONE-09 — genius_tracker_one is
// UUID-identified; the others use a 15-digit IMEI.
const DEVICE_TYPES = ["teltonika_codec8", "gt_pro_v0", "genius_tracker_one"] as const;
type DeviceType = (typeof DEVICE_TYPES)[number];
const UUID_DEVICE_TYPES: ReadonlySet<DeviceType> = new Set(["genius_tracker_one"]);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function deviceStatusTone(status: string): "success" | "info" | "warn" | "danger" | "neutral" {
  switch (status) {
    case "active":
      return "success";
    case "pending_approval":
      return "warn";
    case "rejected":
    case "archived":
      return "danger";
    case "suspended":
      return "warn";
    default:
      return "neutral";
  }
}

export function TenantDevicesCard({ tenantId }: { tenantId: string }) {
  const { t, locale } = useLocale();
  const qc = useQueryClient();

  const query = useQuery<AdminTenantDevicesResponse, Error>({
    queryKey: ["admin", "tenants", tenantId, "devices"],
    queryFn: ({ signal }) =>
      apiGet<AdminTenantDevicesResponse>(
        `/v1/admin/tenants/${tenantId}/devices`,
        undefined,
        signal,
      ),
    staleTime: 30_000,
  });

  // Tenants list — fetched lazily so the picker is populated only
  // when the operator opens it. Cached for 60s.
  const tenantsQuery = useQuery<AdminTenantsResponse, Error>({
    queryKey: ["admin", "tenants", "list-for-move-picker"],
    queryFn: ({ signal }) => apiGet<AdminTenantsResponse>("/v1/admin/tenants", undefined, signal),
    staleTime: 60_000,
    enabled: false,
  });

  // ------- Add device form state ------- //
  const [addOpen, setAddOpen] = useState(false);
  const [addImei, setAddImei] = useState("");
  const [addDeviceType, setAddDeviceType] = useState<DeviceType>("gt_pro_v0");
  const [addError, setAddError] = useState<string | null>(null);

  const addMutation = useMutation({
    mutationFn: async (input: { imei: string; deviceType: DeviceType }) => {
      return apiSend<{ device: AdminTenantDeviceRow }>(
        "POST",
        `/v1/admin/tenants/${tenantId}/devices`,
        input,
      );
    },
    onSuccess: () => {
      setAddImei("");
      setAddDeviceType("gt_pro_v0");
      setAddError(null);
      setAddOpen(false);
      void qc.invalidateQueries({ queryKey: ["admin", "tenants", tenantId, "devices"] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        // GT-DEVICE-UUID-CREATE-APPROVAL-CONSISTENCY-11 (Issue A) —
        // surface the backend's typed reason instead of always
        // falling back to the generic "Try again." message. A fresh
        // Genius Tracker One UUID now creates; a duplicate or a
        // platform-reserved identifier returns a 409 with a typed
        // code, and an identifier that doesn't match the chosen
        // device type returns a 400 `validation_error`.
        if (err.body && typeof err.body === "object" && "error" in (err.body as object)) {
          const code = (err.body as { error?: string }).error ?? "";
          if (code === "imei_conflict") {
            setAddError(t("admin.tenant_360.devices.error.imei_conflict"));
            return;
          }
          if (code === "imei_reserved") {
            setAddError(t("admin.tenant_360.devices.error.imei_reserved"));
            return;
          }
          if (code === "identifier_platform_conflict") {
            // GT-DEVICE-IDENTIFIER-UNIQUENESS-...-12 (Topic A) — the
            // identifier is already claimed by another tenant platform-
            // wide. Admin Console is the platform-owner surface; the
            // backend includes the owning tenant id for triage.
            const owner =
              err.body && typeof err.body === "object" && "ownerTenantId" in (err.body as object)
                ? ((err.body as { ownerTenantId?: string | null }).ownerTenantId ?? null)
                : null;
            setAddError(
              owner
                ? t("admin.tenant_360.devices.error.identifier_platform_conflict_owned", {
                    tenant: owner,
                  })
                : t("admin.tenant_360.devices.error.identifier_platform_conflict"),
            );
            return;
          }
          if (code === "quota_exceeded") {
            setAddError(t("admin.tenant_360.devices.error.quota_exceeded"));
            return;
          }
          if (code === "validation_error") {
            // The backend `validation_error` issues are keyed by path;
            // an `imei` issue means the identifier doesn't match the
            // selected device type. Render the type-aware format hint.
            const issues = (err.body as { issues?: Array<{ path?: string }> }).issues ?? [];
            const identifierIssue = issues.some((i) => (i.path ?? "").includes("imei"));
            if (identifierIssue) {
              setAddError(
                UUID_DEVICE_TYPES.has(addDeviceType)
                  ? t("admin.tenant_360.devices.error.uuid_format")
                  : t("admin.tenant_360.devices.error.imei_format"),
              );
              return;
            }
            setAddError(t("admin.tenant_360.devices.error.device_type_invalid"));
            return;
          }
        }
        setAddError(t("admin.tenant_360.devices.error.create_generic"));
        return;
      }
      setAddError(err.message);
    },
  });

  const addIsUuidType = UUID_DEVICE_TYPES.has(addDeviceType);
  function submitAdd(e: FormEvent) {
    e.preventDefault();
    setAddError(null);
    const identifier = addImei.trim();
    if (addIsUuidType) {
      if (!UUID_RE.test(identifier)) {
        setAddError(t("admin.tenant_360.devices.error.uuid_format"));
        return;
      }
    } else if (!/^[0-9]{15}$/.test(identifier)) {
      setAddError(t("admin.tenant_360.devices.error.imei_format"));
      return;
    }
    addMutation.mutate({ imei: identifier, deviceType: addDeviceType });
  }

  // ------- Archive mutation ------- //
  const archiveMutation = useMutation({
    mutationFn: async (deviceId: string) =>
      apiSend<{ device: AdminTenantDeviceRow }>(
        "DELETE",
        `/v1/admin/tenants/${tenantId}/devices/${deviceId}`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "tenants", tenantId, "devices"] });
    },
  });

  // ------- Sprint 63 (Option B) — hard-delete mutation ------- //
  // Owner GO 2026-05-14: DL-033/036 relaxed. Maps to the new
  // SECURITY DEFINER function via `?confirm=hard`. The IMEI reuse
  // lock survives in `device_imei_reservations` (migration 0068);
  // re-using the same IMEI on the platform thereafter is refused.
  const hardDeleteMutation = useMutation({
    mutationFn: async (deviceId: string) =>
      apiSend<null>("DELETE", `/v1/admin/tenants/${tenantId}/devices/${deviceId}?confirm=hard`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "tenants", tenantId, "devices"] });
    },
  });

  // ------- Restore (unarchive / re-enable) + Disable (suspend) ------- //
  const restoreMutation = useMutation({
    mutationFn: async (deviceId: string) =>
      apiSend<{ device: AdminTenantDeviceRow }>(
        "POST",
        `/v1/admin/tenants/${tenantId}/devices/${deviceId}/restore`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "tenants", tenantId, "devices"] });
    },
  });
  const suspendMutation = useMutation({
    mutationFn: async (deviceId: string) =>
      apiSend<{ device: AdminTenantDeviceRow }>(
        "POST",
        `/v1/admin/tenants/${tenantId}/devices/${deviceId}/suspend`,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "tenants", tenantId, "devices"] });
    },
  });

  // ------- Move mutation + per-row picker state ------- //
  const [movingDeviceId, setMovingDeviceId] = useState<string | null>(null);
  const [moveTargetTenantId, setMoveTargetTenantId] = useState<string>("");
  const [moveError, setMoveError] = useState<string | null>(null);

  const moveMutation = useMutation({
    mutationFn: async (input: { deviceId: string; targetTenantId: string }) =>
      apiSend<{ device: AdminTenantDeviceRow }>(
        "POST",
        `/v1/admin/tenants/${tenantId}/devices/${input.deviceId}/move`,
        { targetTenantId: input.targetTenantId },
      ),
    onSuccess: () => {
      setMovingDeviceId(null);
      setMoveTargetTenantId("");
      setMoveError(null);
      void qc.invalidateQueries({ queryKey: ["admin", "tenants", tenantId, "devices"] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        const body =
          err.body && typeof err.body === "object"
            ? (err.body as { error?: string; message?: string })
            : undefined;
        const code = body?.error;
        // Map every known business-rule code to an actionable message
        // so Move never shows a bare "try again" for a 4xx the API
        // already explained (acceptance: no generic move failure).
        const codeToMessage: Record<string, string> = {
          quota_exceeded: t("admin.tenant_360.devices.error.quota_exceeded"),
          device_same_tenant: t("admin.tenant_360.devices.error.move_same_tenant"),
          target_tenant_not_found: t("admin.tenant_360.devices.error.move_target_missing"),
          device_not_found: t("admin.tenant_360.devices.error.move_not_found"),
          device_pool_not_movable: t("admin.tenant_360.devices.error.move_pool"),
          entity_in_use: t("admin.tenant_360.devices.error.move_in_use"),
          // GT-DEVICE-LIFECYCLE-HISTORY-SAFE-MOVE-06 — devices with only
          // HISTORICAL assignments are now movable, so the backend no longer
          // returns `device_has_history` for Move (only an ACTIVE assignment
          // blocks, via `entity_in_use`). The "Archive it instead" mapping and
          // its now-incorrect i18n key are removed.
          target_imei_exists: t("admin.tenant_360.devices.error.move_target_imei_exists"),
          move_failed: t("admin.tenant_360.devices.error.move_failed"),
        };
        if (code && codeToMessage[code]) {
          setMoveError(codeToMessage[code]);
          return;
        }
        // Unmapped 4xx: surface the server's own actionable message
        // rather than a generic. 5xx keeps the generic (the server
        // body is just "Internal server error.").
        if (body?.message && err.status < 500) {
          setMoveError(body.message);
          return;
        }
        setMoveError(t("admin.tenant_360.devices.error.move_generic"));
        return;
      }
      setMoveError(err.message);
    },
  });

  function openMove(deviceId: string) {
    setMovingDeviceId(deviceId);
    setMoveTargetTenantId("");
    setMoveError(null);
    // Lazy-fetch the tenants list the first time a picker opens.
    void tenantsQuery.refetch();
  }

  function submitMove(deviceId: string) {
    setMoveError(null);
    if (!moveTargetTenantId) {
      setMoveError(t("admin.tenant_360.devices.error.move_pick_tenant"));
      return;
    }
    moveMutation.mutate({ deviceId, targetTenantId: moveTargetTenantId });
  }

  // ------- App-styled inline confirm for archive / restore / disable ------- //
  // Replaces the old browser-native window.confirm so lifecycle actions
  // match the rest of the styled UI and Archive can carry restore-aware
  // copy (Archive is reversible — it is NOT "cannot be undone").
  type LifecycleAction = "archive" | "restore" | "disable";
  const [confirmState, setConfirmState] = useState<{
    deviceId: string;
    imei: string;
    action: LifecycleAction;
  } | null>(null);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  function openConfirm(deviceId: string, imei: string, action: LifecycleAction) {
    setMovingDeviceId(null);
    setConfirmError(null);
    setConfirmState({ deviceId, imei, action });
  }
  function cancelConfirm() {
    setConfirmState(null);
    setConfirmError(null);
  }
  function lifecycleErrorMessage(err: unknown): string {
    if (err instanceof ApiError) {
      const body =
        err.body && typeof err.body === "object"
          ? (err.body as { error?: string; message?: string })
          : undefined;
      const code = body?.error;
      if (code === "lifecycle_unavailable") {
        return t("admin.tenant_360.devices.error.lifecycle_unavailable");
      }
      if (code === "device_not_found") {
        return t("admin.tenant_360.devices.error.device_gone");
      }
      if (body?.message && err.status < 500) return body.message;
      return t("admin.tenant_360.devices.error.action_generic");
    }
    return err instanceof Error ? err.message : t("admin.tenant_360.devices.error.action_generic");
  }
  async function runConfirm() {
    if (!confirmState) return;
    setConfirmError(null);
    const mut =
      confirmState.action === "archive"
        ? archiveMutation
        : confirmState.action === "restore"
          ? restoreMutation
          : suspendMutation;
    try {
      await mut.mutateAsync(confirmState.deviceId);
      setConfirmState(null);
    } catch (err) {
      setConfirmError(lifecycleErrorMessage(err));
    }
  }
  const confirmPending =
    archiveMutation.isPending || restoreMutation.isPending || suspendMutation.isPending;
  function confirmCopy(action: LifecycleAction, imei: string): string {
    if (action === "archive") return t("admin.tenant_360.devices.confirm.archive", { imei });
    if (action === "restore") return t("admin.tenant_360.devices.confirm.restore", { imei });
    return t("admin.tenant_360.devices.confirm.disable", { imei });
  }
  function confirmCta(action: LifecycleAction): string {
    if (action === "archive") return t("admin.tenant_360.devices.action.archive");
    if (action === "restore") return t("admin.tenant_360.devices.action.restore");
    return t("admin.tenant_360.devices.action.disable");
  }

  // Hard-delete (irreversible, reserves IMEI) — identical on both the
  // active and the archived/suspended rows, so render it once.
  function renderHardDelete(d: AdminTenantDeviceRow) {
    // Sprint -07 — the physical tracker identifier may be an IMEI (Pro /
    // Teltonika) or a UUID (Genius Tracker One). Label the confirm field by
    // the derived type so the operator types the right kind of value.
    const identifierFieldLabel =
      d.identifierType === "uuid"
        ? t("admin.devices.identifier_field.uuid")
        : d.identifierType === "imei"
          ? t("admin.devices.identifier_field.imei")
          : t("admin.devices.identifier_field.generic");
    return (
      <DeleteWithConfirm
        triggerLabel={t("admin.devices.action.hard_delete")}
        entityLabel={t("admin.devices.entity_label")}
        identifierFieldLabel={identifierFieldLabel}
        identifierExpected={d.imei}
        warningBody={t("admin.devices.hard_delete.confirm.warning")}
        confirmLabel={t("delete.confirm.confirm")}
        cancelLabel={t("common.cancel")}
        typeToConfirmHint={t("delete.confirm.type_to_confirm")}
        errorMessages={{
          forbidden: t("delete.error.forbidden", { entity: t("admin.devices.entity_label") }),
          notFound: t("admin.devices.hard_delete.error.not_found"),
          entityInUse: t("admin.devices.hard_delete.error.entity_in_use"),
          generic: t("delete.error.generic"),
        }}
        testIdPrefix={`admin-tenant-360-devices-hard-delete-${d.id}`}
        onConfirm={async () => {
          await hardDeleteMutation.mutateAsync(d.id);
        }}
      />
    );
  }

  return (
    <Card elevation={1}>
      <Stack gap={3} data-testid="admin-tenant-360-devices-card">
        <Stack gap={1}>
          <Inline gap={2} align="center" wrap justify="space-between">
            <Stack gap={1}>
              <strong style={{ fontSize: "var(--gt-sys-font-size-lg)" }}>
                {t("admin.tenant_360.devices.title")}
              </strong>
              <span
                style={{
                  color: "var(--gt-sys-text-muted)",
                  fontSize: "var(--gt-sys-font-size-xs)",
                }}
              >
                {t("admin.tenant_360.devices.subtitle")}
              </span>
              <span
                style={{
                  color: "var(--gt-sys-text-muted)",
                  fontSize: "var(--gt-sys-font-size-xs)",
                }}
                data-testid="admin-tenant-360-devices-lifecycle-help"
              >
                {t("admin.tenant_360.devices.lifecycle_help")}
              </span>
            </Stack>
            <Button
              variant="secondary"
              onClick={() => setAddOpen((v) => !v)}
              data-testid="admin-tenant-360-devices-add-toggle"
            >
              {addOpen
                ? t("admin.tenant_360.devices.action.add_cancel")
                : t("admin.tenant_360.devices.action.add")}
            </Button>
          </Inline>
        </Stack>

        {addOpen ? (
          <Card elevation={0}>
            <form onSubmit={submitAdd} data-testid="admin-tenant-360-devices-add-form">
              <Stack gap={2}>
                <strong>{t("admin.tenant_360.devices.add.title")}</strong>
                <Inline gap={2} wrap>
                  <TextInput
                    label={
                      addIsUuidType
                        ? t("admin.tenant_360.devices.add.uuid")
                        : t("admin.tenant_360.devices.add.imei")
                    }
                    value={addImei}
                    onChange={(e) => setAddImei(e.currentTarget.value)}
                    placeholder={
                      addIsUuidType ? "00000000-0000-0000-0000-000000000000" : "123456789012345"
                    }
                    inputMode={addIsUuidType ? "text" : "numeric"}
                    pattern={
                      addIsUuidType
                        ? "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}"
                        : "[0-9]{15}"
                    }
                    maxLength={addIsUuidType ? 36 : 15}
                    minLength={addIsUuidType ? 36 : 15}
                    data-testid="admin-tenant-360-devices-add-imei"
                  />
                  <label style={{ display: "grid", gap: 4 }}>
                    <span style={{ fontSize: 13, color: "var(--gt-sys-text-muted)" }}>
                      {t("admin.tenant_360.devices.add.device_type")}
                    </span>
                    <select
                      value={addDeviceType}
                      onChange={(e) => setAddDeviceType(e.currentTarget.value as DeviceType)}
                      data-testid="admin-tenant-360-devices-add-type"
                      style={{
                        padding: "8px 12px",
                        borderRadius: 8,
                        border: "1px solid var(--gt-sys-border)",
                        background: "var(--gt-sys-surface)",
                      }}
                    >
                      {DEVICE_TYPES.map((t2) => (
                        <option key={t2} value={t2}>
                          {t(`devices.type.${t2}`)}
                        </option>
                      ))}
                    </select>
                  </label>
                </Inline>
                {addError ? (
                  <span role="alert" style={{ color: "var(--gt-sys-status-danger)", fontSize: 13 }}>
                    {addError}
                  </span>
                ) : null}
                <Inline gap={2}>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={addMutation.isPending}
                    data-testid="admin-tenant-360-devices-add-submit"
                  >
                    {addMutation.isPending
                      ? t("admin.tenant_360.devices.add.submitting")
                      : t("admin.tenant_360.devices.add.submit")}
                  </Button>
                  <Button type="button" variant="ghost" onClick={() => setAddOpen(false)}>
                    {t("common.cancel")}
                  </Button>
                </Inline>
              </Stack>
            </form>
          </Card>
        ) : null}

        {query.isLoading ? (
          <span style={{ color: "var(--gt-sys-text-muted)" }}>{t("common.loading")}</span>
        ) : null}

        {query.isError ? (
          <span role="alert" style={{ color: "var(--gt-sys-status-danger)", fontSize: 13 }}>
            {t("admin.tenant_360.devices.error_load")}
          </span>
        ) : null}

        {query.data && Array.isArray(query.data.items) && query.data.items.length === 0 ? (
          <EmptyState
            title={t("admin.tenant_360.devices.empty.title")}
            body={t("admin.tenant_360.devices.empty.body")}
          />
        ) : null}

        {query.data && Array.isArray(query.data.items) && query.data.items.length > 0 ? (
          <Stack gap={2}>
            <Inline gap={2} align="center" wrap>
              <span style={{ fontSize: 13, color: "var(--gt-sys-text-muted)" }}>
                {t("admin.tenant_360.devices.count", { total: query.data.total })}
              </span>
              {query.data.capped ? (
                <span style={{ fontSize: 13, color: "var(--gt-sys-text-muted)" }}>
                  {t("admin.tenant_360.devices.capped")}
                </span>
              ) : null}
            </Inline>
            <div style={{ overflowX: "auto" }}>
              <table
                data-testid="admin-tenant-360-devices-table"
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "var(--gt-sys-font-size-sm)",
                }}
              >
                <thead>
                  <tr>
                    <Th>{t("admin.tenant_360.devices.col.identifier")}</Th>
                    <Th>{t("admin.tenant_360.devices.col.type")}</Th>
                    <Th>{t("admin.tenant_360.devices.col.status")}</Th>
                    <Th>{t("admin.tenant_360.devices.col.last_seen")}</Th>
                    <Th>{t("admin.tenant_360.devices.col.vehicle")}</Th>
                    <Th>{t("admin.tenant_360.devices.col.actions")}</Th>
                  </tr>
                </thead>
                <tbody>
                  {query.data.items.map((d) => (
                    <tr
                      key={d.id}
                      style={{ borderBlockStart: "1px solid var(--gt-sys-border)" }}
                      data-testid={`admin-tenant-360-devices-row-${d.id}`}
                    >
                      <Td>
                        <code style={{ fontSize: 12 }}>{d.imei}</code>
                      </Td>
                      <Td>{d.deviceType}</Td>
                      <Td>
                        <StatePill tone={deviceStatusTone(d.status)} label={d.status} />
                      </Td>
                      <Td>
                        {d.lastSeenAt ? (
                          <time dateTime={d.lastSeenAt}>
                            {formatDateTimeShortShort(d.lastSeenAt, locale)}
                          </time>
                        ) : (
                          <span style={{ color: "var(--gt-sys-text-muted)" }}>—</span>
                        )}
                      </Td>
                      <Td>
                        {d.vehicleId ? (
                          <code style={{ fontSize: 12 }}>{d.vehicleId.slice(0, 8)}…</code>
                        ) : (
                          <span style={{ color: "var(--gt-sys-text-muted)" }}>—</span>
                        )}
                      </Td>
                      <Td>
                        {movingDeviceId === d.id ? (
                          <Stack gap={1}>
                            <select
                              value={moveTargetTenantId}
                              onChange={(e) => setMoveTargetTenantId(e.currentTarget.value)}
                              data-testid={`admin-tenant-360-devices-move-picker-${d.id}`}
                              style={{
                                padding: "4px 8px",
                                borderRadius: 6,
                                border: "1px solid var(--gt-sys-border)",
                                background: "var(--gt-sys-surface)",
                                fontSize: 12,
                                maxInlineSize: 200,
                              }}
                            >
                              <option value="">
                                {t("admin.tenant_360.devices.move.pick_placeholder")}
                              </option>
                              {(tenantsQuery.data?.items ?? [])
                                .filter((tn) => tn.id !== tenantId)
                                .map((tn) => (
                                  <option key={tn.id} value={tn.id}>
                                    {tn.displayName ?? tn.id.slice(0, 8)}
                                  </option>
                                ))}
                            </select>
                            <Inline gap={1}>
                              <Button
                                variant="primary"
                                onClick={() => submitMove(d.id)}
                                disabled={moveMutation.isPending}
                                data-testid={`admin-tenant-360-devices-move-confirm-${d.id}`}
                              >
                                {moveMutation.isPending
                                  ? t("admin.tenant_360.devices.move.submitting")
                                  : t("admin.tenant_360.devices.move.submit")}
                              </Button>
                              <Button
                                variant="ghost"
                                onClick={() => {
                                  setMovingDeviceId(null);
                                  setMoveError(null);
                                }}
                              >
                                {t("common.cancel")}
                              </Button>
                            </Inline>
                            {moveError ? (
                              <span
                                role="alert"
                                style={{ color: "var(--gt-sys-status-danger)", fontSize: 11 }}
                              >
                                {moveError}
                              </span>
                            ) : null}
                          </Stack>
                        ) : confirmState && confirmState.deviceId === d.id ? (
                          <Stack gap={1} data-testid={`admin-tenant-360-devices-confirm-${d.id}`}>
                            <span role="alert" style={{ fontSize: 12 }}>
                              {confirmCopy(confirmState.action, d.imei)}
                            </span>
                            <Inline gap={1}>
                              <Button
                                variant={confirmState.action === "disable" ? "danger" : "primary"}
                                onClick={() => {
                                  void runConfirm();
                                }}
                                disabled={confirmPending}
                                data-testid={`admin-tenant-360-devices-confirm-go-${d.id}`}
                              >
                                {confirmCta(confirmState.action)}
                              </Button>
                              <Button
                                variant="ghost"
                                onClick={cancelConfirm}
                                disabled={confirmPending}
                              >
                                {t("common.cancel")}
                              </Button>
                            </Inline>
                            {confirmError ? (
                              <span
                                role="alert"
                                style={{ color: "var(--gt-sys-status-danger)", fontSize: 11 }}
                              >
                                {confirmError}
                              </span>
                            ) : null}
                          </Stack>
                        ) : d.status === "archived" ||
                          d.status === "disabled" ||
                          d.status === "suspended" ? (
                          // Archived/suspended row: Restore + Delete only.
                          // No Archive (already removed) and no Move
                          // (an archived device must be restored first).
                          <Inline gap={1}>
                            <Button
                              variant="primary"
                              onClick={() => openConfirm(d.id, d.imei, "restore")}
                              data-testid={`admin-tenant-360-devices-restore-${d.id}`}
                            >
                              {t("admin.tenant_360.devices.action.restore")}
                            </Button>
                            {renderHardDelete(d)}
                          </Inline>
                        ) : (
                          // Active row: Move, Archive (reversible), Disable
                          // (suspend — distinct from Archive), Delete.
                          <Inline gap={1}>
                            <Button
                              variant="ghost"
                              onClick={() => openMove(d.id)}
                              data-testid={`admin-tenant-360-devices-move-open-${d.id}`}
                            >
                              {t("admin.tenant_360.devices.action.move")}
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => openConfirm(d.id, d.imei, "archive")}
                              data-testid={`admin-tenant-360-devices-archive-${d.id}`}
                            >
                              {t("admin.tenant_360.devices.action.archive")}
                            </Button>
                            <Button
                              variant="ghost"
                              onClick={() => openConfirm(d.id, d.imei, "disable")}
                              data-testid={`admin-tenant-360-devices-disable-${d.id}`}
                            >
                              {t("admin.tenant_360.devices.action.disable")}
                            </Button>
                            {renderHardDelete(d)}
                          </Inline>
                        )}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Stack>
        ) : null}
      </Stack>
    </Card>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      style={{
        textAlign: "start",
        padding: "var(--gt-sys-space-2) var(--gt-sys-space-3)",
        color: "var(--gt-sys-text-muted)",
        fontWeight: "var(--gt-sys-font-weight-medium)",
        fontSize: "var(--gt-sys-font-size-xs)",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        borderBlockEnd: "1px solid var(--gt-sys-border)",
      }}
    >
      {children}
    </th>
  );
}

function Td({ children }: { children: React.ReactNode }) {
  return (
    <td style={{ padding: "var(--gt-sys-space-2) var(--gt-sys-space-3)", verticalAlign: "middle" }}>
      {children}
    </td>
  );
}
